<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import DataLayer from '@services/data-layer/index'
import SharedCarousel from '@components/carousel/shared-carousel'
import SharedCarouselSlide from '@components/carousel/shared-carousel-slide'
import MixLargeTile from '@components/mix-large-tile'
import MixGridTile from '@components/mix-grid-tile'
import PremiumBanner from '@components/premium-banner'

const VISIBLE_TRENDING_MIXES = 6

const props = defineProps({
    nowPlaying: {
        type: Object,
        default: () => ({}),
    },
    channel: {
        type: Object,
        default: () => ({}),
    },
})

const route = useRoute()

const trendingMixes = ref([])

const trendingTitle = computed(() => `Trending on ${props.channel?.title || ''}`.trim())

const displayedMixes = computed(() => trendingMixes.value.slice(0, VISIBLE_TRENDING_MIXES))

const bannerSource = computed(() => `radio-channel-${route.params.channelId}`)

async function loadTrendingMixes() {
    const response = await DataLayer.mixes.getPopularByChannel(route.params.channelId)
    trendingMixes.value = response.body
}

watch(() => route.params.channelId, loadTrendingMixes)

onMounted(loadTrendingMixes)
</script>

<template>
    <BaseContent>
        <BaseSection
            padded
            title="Now Playing"
        >
            <MixLargeTile
                v-if="nowPlaying"
                :mix="nowPlaying.mixes"
                :channel="channel"
            >
                <template #button>
                    <BaseButtonPlay
                        :resource="channel"
                        size="l"
                    />
                </template>
            </MixLargeTile>
        </BaseSection>
        <div class="premium-banner-inset">
            <PremiumBanner :source="bannerSource" />
        </div>
        <BaseSection
            v-if="displayedMixes.length"
            :title="trendingTitle"
            padded
        >
            <SharedCarousel
                :per-page-custom="[[0, 2], [768, 3], [1025, 6]]"
                :pagination-enabled="false"
                :scroll-per-page="false"
                :min-swipe-distance="100"
                :space-padding="0"
                :mouse-drag="displayedMixes.length > 1"
                class="carousel"
            >
                <SharedCarouselSlide
                    v-for="(mix, index) in displayedMixes"
                    :key="`channel-trending-${mix.id}-${index}`"
                    class="slide"
                >
                    <MixGridTile
                        :mix="mix"
                        :index="index"
                    />
                </SharedCarouselSlide>
            </SharedCarousel>
        </BaseSection>
    </BaseContent>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// Align the banner with the padded sections above/below it (container width +
// side gutters), and reproduce the section vertical rhythm — a bare element
// between two sections breaks the `.section + .section` margin chain, so the
// top/bottom gaps are set explicitly here. Figma 822-442.
.premium-banner-inset {
    @include container;
    @include sidePadding;

    margin-top: $size-grid-padding * 3;
    margin-bottom: $size-grid-padding * 3;

    @include desktop {
        margin-top: $s-h;
        margin-bottom: $s-h;
    }
}

.carousel {
    position: relative;
    margin: 0 (-$size-grid-padding);
    overflow: visible;
}

.slide {
    position: relative;
    padding: 0 $size-grid-padding;

    &:hover,
    &:focus-within {
        z-index: 2;
    }
}

:deep(.carousel .shared-carousel-viewport),
:deep(.carousel .swiper-wrapper),
:deep(.carousel .shared-carousel-slide) {
    overflow: visible;
}
</style>
